import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../types/domain';
import { authService } from '../lib/auth/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Decodifica JWT sem validar assinatura (confia no backend)
function decodeToken(token: string) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('❌ [decodeToken] Token não tem 3 partes. Partes:', parts.length);
      return null;
    }
    // ✅ Usar atob() para navegador, não Buffer (que é Node.js)
    const decoded = JSON.parse(atob(parts[1]));
    console.log('✅ [decodeToken] Token decodificado. Exp:', decoded.exp);
    return decoded;
  } catch (e) {
    console.error('❌ [decodeToken] Erro ao decodificar:', e);
    return null;
  }
}

// Verifica se token está expirado
function isTokenExpired(token: string) {
  console.log('🕐 [isTokenExpired] INICIANDO VERIFICAÇÃO');
  console.log('   Token recebido:', token?.substring(0, 50) + '...');

  const decoded = decodeToken(token);
  console.log('   Decoded:', decoded);

  if (!decoded) {
    console.error('❌ [isTokenExpired] NÃO CONSEGUIU DECODIFICAR! Retornando TRUE (expirado)');
    return true;
  }

  if (!decoded.exp) {
    console.error('❌ [isTokenExpired] Token sem campo exp! Retornando TRUE (expirado)');
    console.log('   Payload decodificado:', JSON.stringify(decoded).substring(0, 100));
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = decoded.exp - now;
  const daysRemaining = expiresIn / (60 * 60 * 24);

  console.log('✅ [isTokenExpired] Verificação completa:');
  console.log(`   Exp: ${decoded.exp} (timestamp)`);
  console.log(`   Now: ${now} (timestamp)`);
  console.log(`   Segundos restantes: ${expiresIn}`);
  console.log(`   Dias restantes: ${daysRemaining.toFixed(2)}`);
  console.log(`   Expirado? ${expiresIn < 0}`);

  return expiresIn < 0;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Refresh é feito automaticamente pelo interceptador em client.ts
  // Esta função fica aqui por compatibilidade, mas não faz nada
  const refreshAccessToken = useCallback(async () => {
    return true; // Já é feito automaticamente
  }, []);

  // Verificação inicial - UMA VEZ ao montar
  useEffect(() => {
    const initAuth = () => {
      console.log('🔐 [AuthContext] Inicializando autenticação...');
      const accessToken = authService.getAccessToken();
      const savedUser = authService.getUser();

      console.log(`🔐 [AuthContext] Token recuperado: ${accessToken ? 'SIM' : 'NÃO'}`);
      console.log(`🔐 [AuthContext] User recuperado: ${savedUser ? 'SIM' : 'NÃO'}`);

      // Sem token - não autenticado
      if (!accessToken) {
        console.log('❌ [AuthContext] Nenhum token encontrado');
        setIsLoading(false);
        return;
      }

      // Token expirado - limpar e sair
      if (isTokenExpired(accessToken)) {
        console.error('❌ [AuthContext] TOKEN EXPIRADO! Deslogando usuário!');
        authService.clearTokens();
        setIsLoading(false);
        return;
      }

      console.log('✅ [AuthContext] Token válido! Restaurando sessão...');

      // Token válido - restaurar user e marcar autenticado
      if (savedUser) {
        setUser(savedUser);
        setIsAuthenticated(true);
        console.log('✅ [AuthContext] User restaurado do localStorage');
      } else {
        // Token existe mas user não foi salvo - marca como autenticado mesmo assim
        setIsAuthenticated(true);
        console.log('💡 [AuthContext] Token válido sem user data');
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = (token: string, refreshToken: string, userData: User) => {
    console.log('✅ [AuthContext] Login realizado');
    authService.setTokens(token, refreshToken, userData);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    console.log('👋 [AuthContext] Logout realizado');
    authService.clearTokens();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, refreshAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
